import type { InventoryClosureRecord } from './inventoryClosureStorage';

export const POC_INVENTORY_STOCK_IMPUTATION_EVENT = 'poc-inventory-stock-imputation-changed';

const STORAGE_KEY = 'poc-app-inventory-stock-imputations-v1';

export interface InventoryLineImputationInput {
  referencePiece: string;
  quantiteTheorique: number;
  quantiteComptee: number;
}

export interface InventoryStockImputationRecord {
  inventoryReference: string;
  referencePiece: string;
  quantiteTheorique: number;
  quantiteComptee: number;
  ecart: number;
  appliedAt: string;
}

interface StorageShape {
  /** Dernière imputation par référence d’inventaire. */
  byInventoryReference: Record<string, InventoryStockImputationRecord[]>;
}

const emptyShape = (): StorageShape => ({ byInventoryReference: {} });

function readStorage(): StorageShape {
  if (typeof window === 'undefined') {
    return emptyShape();
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);

    if (!raw) {
      return emptyShape();
    }

    const parsed = JSON.parse(raw) as unknown;

    if (!parsed || typeof parsed !== 'object') {
      return emptyShape();
    }

    const obj = parsed as { byInventoryReference?: unknown };

    if (!obj.byInventoryReference || typeof obj.byInventoryReference !== 'object') {
      return emptyShape();
    }

    return {
      byInventoryReference: obj.byInventoryReference as StorageShape['byInventoryReference'],
    };
  } catch {
    return emptyShape();
  }
}

function writeStorage(data: StorageShape): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  window.dispatchEvent(new CustomEvent(POC_INVENTORY_STOCK_IMPUTATION_EVENT));
}

export function loadInventoryStockImputations(
  inventoryReference: string,
): InventoryStockImputationRecord[] {
  return readStorage().byInventoryReference[inventoryReference] ?? [];
}

/** Écarts cumulés par référence constructeur (toutes clôtures confondues). */
export function loadStockEcartByReferenceConstructeur(): Record<string, number> {
  const data = readStorage();
  const out: Record<string, number> = {};

  for (const records of Object.values(data.byInventoryReference)) {
    for (const record of records) {
      const key = record.referencePiece.trim().toUpperCase();

      if (key === '') {
        continue;
      }

      out[key] = (out[key] ?? 0) + record.ecart;
    }
  }

  return out;
}

export interface StockImputationApplyResult {
  records: InventoryStockImputationRecord[];
  totalEcart: number;
  linesWithEcart: number;
}

export function applyInventoryStockImputation(
  inventoryReference: string,
  lines: InventoryLineImputationInput[],
  closure: InventoryClosureRecord,
): StockImputationApplyResult {
  const appliedAt = closure.closedAt;
  const records: InventoryStockImputationRecord[] = lines.map((line) => ({
    inventoryReference,
    referencePiece: line.referencePiece.trim(),
    quantiteTheorique: line.quantiteTheorique,
    quantiteComptee: line.quantiteComptee,
    ecart: line.quantiteComptee - line.quantiteTheorique,
    appliedAt,
  }));

  const data = readStorage();

  data.byInventoryReference[inventoryReference] = records;
  writeStorage(data);

  const totalEcart = records.reduce((sum, record) => sum + record.ecart, 0);
  const linesWithEcart = records.filter((record) => record.ecart !== 0).length;

  return { records, totalEcart, linesWithEcart };
}

export interface StockPieceRowForImputation {
  idElementStock: string;
  referenceConstructeur: string;
  referencePieceDetachee: string;
  statutElementStock: string;
  nomStock: string;
  siteAssocie: string;
  fournisseur: string;
  constructeur: string;
  etatPiece: string;
  idPieceDetachee: string;
  referenceFournisseur: string;
  idFournisseur: string;
  idStock: string;
  referenceDossier: string;
  idDossier: string;
  emplacementStock: string;
  prixVenteTtc: string;
  prixAchatHt: string;
  dateEntreeStock: string;
  dateDerniereMiseAJour: string;
  nomPiece: string;
}

export function applyStockEcartAdjustments<T extends StockPieceRowForImputation>(
  baseRows: T[],
): T[] {
  const ecartByRef = loadStockEcartByReferenceConstructeur();
  const keys = Object.keys(ecartByRef);

  if (keys.length === 0) {
    return baseRows;
  }

  let rows = [...baseRows];

  for (const refKey of keys) {
    const delta = ecartByRef[refKey];

    if (delta === 0) {
      continue;
    }

    const matching = rows.filter(
      (row) => row.referenceConstructeur.trim().toUpperCase() === refKey,
    );

    if (matching.length === 0) {
      continue;
    }

    if (delta > 0) {
      const template = matching[0];
      const nowIso = new Date().toISOString();

      for (let index = 0; index < delta; index += 1) {
        rows.push({
          ...template,
          idElementStock: `imputed-${refKey}-${nowIso}-${index}`,
          statutElementStock: 'Disponible',
          dateDerniereMiseAJour: nowIso,
        });
      }
    } else {
      let remaining = -delta;

      rows = rows.filter((row) => {
        if (
          remaining > 0 &&
          row.referenceConstructeur.trim().toUpperCase() === refKey &&
          row.statutElementStock === 'Disponible' &&
          !row.idElementStock.startsWith('imputed-')
        ) {
          remaining -= 1;

          return false;
        }

        return true;
      });
    }
  }

  return rows;
}
